'use client'

import { Fragment, useEffect, useState, useTransition } from 'react'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'
import { useRouter, usePathname } from '@/lib/next-shim'
import { useBooking } from '../model/bookingContext'
import { saveStateToStorage } from '../lib/bookingStorage'
import { Dialog, Transition } from '@headlessui/react'
import { ProfileGate } from './ProfileGate'

interface AuthenticationGateProps {
  children: React.ReactNode
  returnPath?: string
  onAuthenticationRequired?: () => void
}

export function AuthenticationGate({ children, returnPath, onAuthenticationRequired }: AuthenticationGateProps) {
  const { state, dispatch } = useBooking()
  const { status } = useSession()
  const router = useRouter()
  const pathname = usePathname()
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false)
  const [isTransitioning, startTransition] = useTransition()

  useEffect(() => {
    // Only proceed if auth status is definitively known (not loading)
    if (status === 'loading') {
      console.log('AuthenticationGate - Auth status is loading')
      return
    }

    // If already authenticated
    if (status === 'authenticated') {
      setHasCheckedAuth(true)
      return
    }

    // Prevent redirection loops by checking if we're already on an auth page
    const isAuthPage = pathname?.includes('/auth/') || pathname?.includes('/register') || pathname?.includes('/signin')

    // Only redirect if:
    // 1. We're not already on an auth page
    // 2. The status is definitely unauthenticated
    // 3. We haven't already redirected for this specific route
    if (!isAuthPage && status === 'unauthenticated') {
      // If there's a callback to run before redirecting, run it
      if (onAuthenticationRequired) {
        console.log('AuthenticationGate - Running onAuthenticationRequired callback')
        onAuthenticationRequired()
      }

      // Persist booking state before auth redirect (survives new tab from email verification)
      saveStateToStorage(state)

      // Redirect to the sign-in page with the return path
      const callbackUrl = returnPath || pathname || '/unknown'
      const encodedCallbackUrl = encodeURIComponent(callbackUrl)
      const signInUrl = `/register?callbackUrl=${encodedCallbackUrl}`
      startTransition(() => {
        router.push(signInUrl)
      })
    }

    setHasCheckedAuth(true)
  }, [status, router, returnPath, onAuthenticationRequired, pathname, dispatch, state.meetingLinkSlug])

  // Show nothing while checking authentication
  if (status === 'loading' || !hasCheckedAuth || isTransitioning) {
    return <AuthenticationRedirecting />
  }

  // If authenticated, check profile completeness then show children
  if (status === 'authenticated') {
    return <ProfileGate>{children}</ProfileGate>
  }

  // If not authenticated, show nothing (will redirect)
  return null
}

function AuthenticationRedirecting() {
  return (
    <Transition.Root show as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div>
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-white" />
                </div>
                <p className="text-white">Authenticating...</p>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
